import React, { useEffect } from 'react';
import { FaTag, FaPrint } from 'react-icons/fa';
import { getDisplayPrice, computeUnitPriceInfo } from './pricingUtils';

const PriceLabelsPreview = ({
  show,
  onClose,
  previewProducts,
  labelSettings,
  setLabelSettings,
  settingsSaved,
  handleSaveSettings,
  // Copy duplication
  copyMultiplier,
  setCopyMultiplier,
  selectedForCopy,
  handleToggleCopySelection,
  handleSelectAllForCopy,
  handleDeselectAllForCopy,
  // Buffer interaction
  labelBuffer,
  handleLoadBufferToPreview,
  addToBuffer,
  setShowBuffer,
  setShowPreview,
  // Print
  handlePrintLabels,
  // Measure units for label calculation
  measureUnits,
}) => {
  // Auto-dopasowanie etykiet do rozmiaru 4x2cm - IDENTYCZNE jak w oknie druku
  // (LocationPricingPage handlePrintLabels). Zmniejsza tresc TYLKO gdy sie nie miesci,
  // wiec nic nie zostaje ucinane, a podglad pokazuje dokladnie to co wyjdzie na druku.
  useEffect(() => {
    if (!show) return undefined;
    const fit = () => {
      document.querySelectorAll('.cenowka-label-outer').forEach((lab) => {
        const inner = lab.querySelector('.cenowka-label-inner');
        if (!inner) return;
        inner.style.transform = '';
        const cs = getComputedStyle(lab);
        const availH = lab.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
        const availW = lab.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        const s = Math.min(availH / inner.scrollHeight, availW / inner.scrollWidth, 1);
        if (s < 1) inner.style.transform = `scale(${s})`;
      });
    };
    const t = setTimeout(fit, 60);
    return () => clearTimeout(t);
  }, [show, previewProducts, labelSettings, copyMultiplier, selectedForCopy]);

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1050,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '12px', width: '95%', maxWidth: '1200px',
        maxHeight: '95vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          border: '1px solid var(--purple)',
          color: 'var(--purple)', padding: '1rem 1.5rem', borderRadius: '12px 12px 0 0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
              {'🏷️'} Podgląd cenówek
            </h5>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              {previewProducts.length} produktów wybranych do druku
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', color: 'var(--text-secondary)',
            width: '32px', height: '32px',
            fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>{'×'}</button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {/* Ustawienia */}
          <div style={{
            backgroundColor: 'var(--bg)', borderRadius: '8px', padding: '0.75rem',
            marginBottom: '1rem', border: '1px solid var(--border)'
          }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--purple)', marginBottom: '0.5rem' }}>
              {'⚙️'} Ustawienia etykiet
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              {[
                { id: 'includeManufacturer', label: 'Producent' },
                { id: 'includeSimplifiedName', label: 'Nazwa uproszczona' },
                { id: 'includeQuantity', label: 'Ilość w opakowaniu' },
                { id: 'includePrice', label: 'Cena aktualna' },
                { id: 'includeSpecialPrice', label: 'Cena specjalna' },
                { id: 'includeWeight', label: 'Waga produktu' }
              ].map(opt => (
                <label key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={labelSettings[opt.id]}
                    onChange={(e) => setLabelSettings({...labelSettings, [opt.id]: e.target.checked})}
                    style={{ accentColor: 'var(--purple)' }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Font sizes */}
          <div style={{
            background: 'linear-gradient(135deg, var(--info-light), var(--info-light))', borderRadius: '8px',
            padding: '0.75rem', marginBottom: '1rem', border: '1px solid var(--info)'
          }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--info)', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{'🔤'} Rozmiary czcionek (px) i odstępy</span>
              {settingsSaved && (
                <span style={{ fontSize: '10px', color: 'var(--success)', fontWeight: '500' }}>{'✓'} Zapisano</span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
              {[
                { label: 'Cena:', key: 'priceFontSize', min: 12, max: 32, defaultVal: 22 },
                { label: 'Nazwa:', key: 'productNameFontSize', min: 6, max: 16, defaultVal: 10 },
                { label: 'Producent:', key: 'manufacturerFontSize', min: 5, max: 14, defaultVal: 8 },
                { label: 'Jednostki:', key: 'unitsFontSize', min: 6, max: 14, defaultVal: 10 },
              ].map(item => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '10px', color: 'var(--info)', minWidth: '70px' }}>{item.label}</span>
                  <input type="number" min={item.min} max={item.max} value={labelSettings[item.key]}
                    onChange={(e) => setLabelSettings({...labelSettings, [item.key]: parseInt(e.target.value) || item.defaultVal})}
                    style={{ width: '50px', padding: '2px 4px', fontSize: '11px', border: '1px solid var(--info)', borderRadius: '4px', textAlign: 'center' }} />
                  <span style={{ fontSize: '10px', color: 'var(--info)' }}>px</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: 'span 2', marginTop: '0.25rem', borderTop: '1px solid var(--info-light)', paddingTop: '0.5rem' }}>
                <span style={{ fontSize: '10px', color: 'var(--info)', minWidth: '100px' }}>Odstęp wierszy:</span>
                <input type="number" min="0" max="5" step="0.5" value={labelSettings.lineSpacing}
                  onChange={(e) => setLabelSettings({...labelSettings, lineSpacing: parseFloat(e.target.value) || 1})}
                  style={{ width: '50px', padding: '2px 4px', fontSize: '11px', border: '1px solid var(--info)', borderRadius: '4px', textAlign: 'center' }} />
                <span style={{ fontSize: '10px', color: 'var(--info)' }}>mm</span>
              </div>
              <div style={{ gridColumn: 'span 2', marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button onClick={handleSaveSettings}
                  style={{
                    padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                    border: '1px solid var(--info)', color: 'var(--info)'
                  }}>
                  {settingsSaved ? '✓ Zapisano!' : '💾 Zapisz ustawienia'}
                </button>
              </div>
            </div>
          </div>

          {/* Copy duplication options */}
          <div style={{
            background: 'linear-gradient(135deg, var(--warning-light), var(--warning-light))', borderRadius: '8px',
            padding: '0.75rem', marginBottom: '1rem', border: '1px solid var(--warning)'
          }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--warning)' }}>
                {'📦'} Powielanie:
              </div>
              <select value={copyMultiplier} onChange={(e) => setCopyMultiplier(parseInt(e.target.value))}
                style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--warning)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}>
                {[1,2,4,6,8,10].map(n => (
                  <option key={n} value={n}>{n}x {n === 1 ? '(pojedyncza)' : ''}</option>
                ))}
              </select>
              <button onClick={handleSelectAllForCopy} style={{
                padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)',
                color: 'var(--success)'
              }}>{'✓'} Zaznacz wszystkie</button>
              <button onClick={handleDeselectAllForCopy} style={{
                padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)',
                color: 'var(--danger)'
              }}>{'✕'} Odznacz</button>
              <span style={{ fontSize: '10px', padding: '0.25rem 0.5rem', border: '1px solid var(--purple)', color: 'var(--purple)' , borderRadius: '4px' }}>
                Zaznaczono: {selectedForCopy.size} | Łącznie: {previewProducts.reduce((sum, p) => sum + (selectedForCopy.has(p.id) ? copyMultiplier : 1), 0)}
              </span>
            </div>
          </div>

          {/* Label grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
            {previewProducts.flatMap((product) => {
              const priceInfo = getDisplayPrice(product);
              const displayPrice = parseFloat(product.displayPrice || priceInfo.price || 0);
              const { packageInfo, unitPriceText } = computeUnitPriceInfo(product, displayPrice, measureUnits);
              const isSelectedForCopy = selectedForCopy.has(product.id);
              const multiplier = isSelectedForCopy ? copyMultiplier : 1;

              return Array.from({ length: multiplier }, (_, copyIndex) => (
                <div key={`${product.id}-${copyIndex}`} style={{
                  backgroundColor: 'var(--card-bg)', borderRadius: '8px', padding: '0.5rem',
                  border: isSelectedForCopy && copyIndex === 0 ? '2px solid var(--warning)' : '1px solid var(--border)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  opacity: copyIndex > 0 ? 0.7 : 1
                }}>
                  {copyIndex === 0 && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', marginBottom: '0.5rem', cursor: 'pointer', color: 'var(--purple)' }}>
                      <input type="checkbox" checked={selectedForCopy.has(product.id)}
                        onChange={() => handleToggleCopySelection(product.id)}
                        style={{ accentColor: 'var(--warning)' }} />
                      Powiel {copyMultiplier}x
                      {isSelectedForCopy && copyMultiplier > 1 && (
                        <span style={{ fontSize: '9px', padding: '1px 4px', backgroundColor: 'var(--warning)', color: 'var(--text)', borderRadius: '3px' }}>+{copyMultiplier-1}</span>
                      )}
                    </label>
                  )}
                  {/* Label 4x2cm — 1:1 z oknem druku (LocationPricingPage handlePrintLabels).
                      Fizyczna etykieta jest BIALA z czarna ramka, wiec kolory sa SZTYWNE
                      (nie tokeny motywu) - inaczej podglad w ciemnym motywie klamalby. */}
                  <div className="cenowka-label-outer" style={{
                    width: '4cm', height: '2cm', border: '1px solid #000',
                    padding: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
                    justifyContent: 'center', textAlign: 'center', backgroundColor: '#fff', margin: '0 auto',
                    overflow: 'hidden', fontFamily: 'Arial, sans-serif'
                  }}>
                    <div className="cenowka-label-inner" style={{
                      width: '100%', display: 'flex', flexDirection: 'column',
                      alignItems: 'stretch', transformOrigin: 'center center'
                    }}>
                    {(() => {
                      // Pionowa siatka: cena / nazwa / producent (pelna szerokosc), a na dole
                      // gramatura OBOK przelicznika (2 kolumny, pionowa kreska). Linia pod kazdym
                      // wierszem oprocz ostatniego. 1:1 z oknem druku (LocationPricingPage).
                      const rows = [];
                      if (labelSettings.includePrice) rows.push({ k: 'price', kind: 'text', el: `${parseFloat(product.displayPrice || priceInfo.price || 0).toFixed(2)} zł`, fs: labelSettings.priceFontSize, fw: 'bold', color: '#000' });
                      if (labelSettings.includeSimplifiedName) rows.push({ k: 'name', kind: 'text', el: product.simplifiedName || product.nazwa, fs: labelSettings.productNameFontSize, fw: 'bold', color: '#000', wrap: true });
                      if (labelSettings.includeManufacturer && product.manufacturer) rows.push({ k: 'mfr', kind: 'text', el: product.manufacturer, fs: labelSettings.manufacturerFontSize, fw: 'bold', color: '#1d4ed8', nowrap: true });
                      if (packageInfo || unitPriceText) rows.push({ k: 'units', kind: 'units' });
                      return rows.map((r, i) => {
                        const notLast = i < rows.length - 1;
                        if (r.kind === 'units') {
                          return (
                            <div key={r.k} style={{ display: 'flex', alignItems: 'stretch', borderBottom: notLast ? '1px solid #000' : 'none' }}>
                              {packageInfo && <div style={{ flex: 1, padding: '0.3mm 1mm', fontSize: `${labelSettings.unitsFontSize}px`, fontWeight: 600, color: '#000', whiteSpace: 'nowrap', lineHeight: '1.05', borderRight: unitPriceText ? '1px solid #000' : 'none' }}>{packageInfo}</div>}
                              {unitPriceText && <div style={{ flex: 1, padding: '0.3mm 1mm', fontSize: `${labelSettings.unitsFontSize}px`, fontWeight: 600, color: '#000', whiteSpace: 'nowrap', lineHeight: '1.05' }}>{unitPriceText}</div>}
                            </div>
                          );
                        }
                        return (
                          <div key={r.k} style={{
                            padding: '0.35mm 1mm', lineHeight: '1.05', fontSize: `${r.fs}px`, fontWeight: r.fw, color: r.color,
                            whiteSpace: r.nowrap ? 'nowrap' : 'normal', overflow: r.nowrap ? 'hidden' : 'visible',
                            wordBreak: r.wrap ? 'break-word' : 'normal',
                            borderBottom: notLast ? '1px solid #000' : 'none'
                          }}>{r.el}</div>
                        );
                      });
                    })()}
                    </div>
                  </div>
                </div>
              ));
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.75rem 1rem', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: 'var(--bg)', borderRadius: '0 0 12px 12px'
        }}>
          <select value={labelSettings.fontSize} onChange={(e) => setLabelSettings({...labelSettings, fontSize: e.target.value})}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}>
            <option value="small">Mała czcionka</option>
            <option value="medium">Średnia czcionka</option>
            <option value="large">Duża czcionka</option>
          </select>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onClose} style={{
              padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)',
              color: 'var(--text-muted)'
            }}>Zamknij</button>
            <button onClick={handleLoadBufferToPreview} disabled={labelBuffer.length === 0} style={{
              padding: '6px 12px', fontSize: '12px',
              border: `1px solid ${labelBuffer.length === 0 ? 'var(--border)' : 'var(--warning)'}`,
              color: labelBuffer.length === 0 ? 'var(--text-muted)' : 'var(--warning)',
              opacity: labelBuffer.length === 0 ? 0.6 : 1
            }}>
              <FaTag style={{ marginRight: '4px' }} />Załaduj z bufora ({labelBuffer.length})
            </button>
            <button onClick={() => { previewProducts.forEach(product => addToBuffer(product)); setShowPreview(false); setShowBuffer(true); }}
              style={{
                padding: '6px 12px', fontSize: '12px',
                border: '1px solid var(--cyan)', color: 'var(--cyan)'
              }}>
              <FaTag style={{ marginRight: '4px' }} />Do bufora
            </button>
            <button onClick={handlePrintLabels} style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--success)', color: 'var(--success)' , cursor: 'pointer'
            }}>
              <FaPrint style={{ marginRight: '4px' }} />Drukuj
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceLabelsPreview;
